import React, { useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { ProgressIndicator } from "components/designV2/progressIndicator/progressIndicator.component";
import { ColorV2 } from "config/colors";
import { isValidPhoneNumber } from "web-apps/company/components/staff/components/addStaffPanel/addStaffPanel.validator";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  ActionsRow,
  BackButton,
  ContinueButton,
  FieldsWrapper,
  LoadingWrapper,
  StepLabel,
  StyledForm,
  StyledTitle,
} from "./contactForm.styled";

const MAX_PHONE_DIGITS = 15;

const CREATE_COMPANY_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  disabledBackgroundColor: ColorV2.PositiveGreenLight,
  disabledTextColor: ColorV2.White,
  disabledBorderColor: "transparent",
};

type Props = {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  firstName: string;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  lastName: string;
  setLastName: React.Dispatch<React.SetStateAction<string>>;
  contactPersonEmail: string;
  contactPersonPhoneNumber: string;
  setContactPersonPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  register: () => Promise<void>;
};

export const ContactForm: React.FC<Props> = ({
  page,
  setPage,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  contactPersonEmail,
  contactPersonPhoneNumber,
  setContactPersonPhoneNumber,
  register,
}) => {
  const { t } = useTranslation();
  const requiredLabel = t("System.Required").toLowerCase();
  const [companyState] = useCompanyStore();
  const [showPhoneNumberError, setShowPhoneNumberError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasValue = (value: string): boolean => value.trim().length > 0;

  const isPhoneNumberInvalid = !isValidPhoneNumber(contactPersonPhoneNumber);

  const validate = (): boolean => {
    return (
      hasValue(firstName) &&
      hasValue(lastName) &&
      hasValue(contactPersonPhoneNumber) &&
      !isPhoneNumberInvalid
    );
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (validate()) {
      setIsSubmitting(true);
      try {
        await register();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <StepLabel>
        {t("RegisterCompany.StepOfTotal", { step: 2, total: 2 })}
      </StepLabel>
      <ProgressIndicator steps={2} currentStep={2} />
      {companyState.isLoading ? (
        <LoadingWrapper>
          <CircularProgress color="inherit" />
        </LoadingWrapper>
      ) : (
        <>
          <StyledTitle>{t("RegisterCompany.ContactPerson")}</StyledTitle>
          <FieldsWrapper>
            <InputField
              label={`${t("RegisterCompany.FirstName")} (${requiredLabel})`}
              type="text"
              value={firstName}
              placeholder={t("RegisterCompany.FirstName")}
              fullWidth
              onChange={setFirstName}
              onClear={() => setFirstName("")}
            />
            <InputField
              label={`${t("RegisterCompany.LastName")} (${requiredLabel})`}
              type="text"
              value={lastName}
              placeholder={t("RegisterCompany.LastName")}
              fullWidth
              onChange={setLastName}
              onClear={() => setLastName("")}
            />
            <InputField
              label={`${t("RegisterCompany.PhoneNumber")} (${requiredLabel})`}
              type="text"
              value={contactPersonPhoneNumber}
              fullWidth
              maxLength={MAX_PHONE_DIGITS}
              hintText={t("Staff.AddStaffPanelHintPhoneNumberCountryCode")}
              hasError={showPhoneNumberError && isPhoneNumberInvalid}
              errorText={t("Staff.AddStaffPanelPhoneNumberInvalid")}
              onChange={(value) => {
                setContactPersonPhoneNumber(
                  value.replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS),
                );
              }}
              onBlur={() => setShowPhoneNumberError(true)}
              onClear={() => {
                setContactPersonPhoneNumber("");
                setShowPhoneNumberError(false);
              }}
            />
          </FieldsWrapper>
          <ActionsRow>
            <BackButton
              type="button"
              disabled={page !== 1}
              label={t("General.Back")}
              onClick={() => {
                setPage(page - 1);
              }}
            />
            <ContinueButton
              type="submit"
              label={t("RegisterCompany.CreateCompany")}
              disabled={
                !validate() ||
                page !== 1 ||
                companyState.isLoading ||
                isSubmitting
              }
              isLoading={companyState.isLoading || isSubmitting}
              styleOverrides={CREATE_COMPANY_BUTTON_STYLES}
            />
          </ActionsRow>
        </>
      )}
    </StyledForm>
  );
};
